import {
  Box,
  Button,
  CloseButton,
  Flex,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
  rem,
} from "@mantine/core";
import { IconHash, IconUser } from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { Patient } from "types/patientTypes";
import PatientInfoCard from "@components/new_test_ui/new_test_steps/common/PatientInfoCard";
import { displayPatientName, formatDateOnly } from "@utils/CommonUtil";
import { getPatient } from "@utils/api-communication/RestApiCli";
import { useTranslation } from "react-i18next";

interface ChangePatientModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (patient: Patient) => void;
}

export default function ChangePatientModal({
  opened,
  onClose,
  onConfirm,
}: ChangePatientModalProps) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [results, setResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [listOpen, setListOpen] = useState(false);

  useEffect(() => {
    if (searchValue.length > 2) {
      setListOpen(true);
      getPatient(
        { name: searchValue.toLowerCase(), uuid: searchValue.toLowerCase() },
        (response) => {
          setResults(response.data ?? []);
        },
      );
    } else {
      setResults([]);
      setListOpen(false);
    }
  }, [searchValue]);

  function handleClose() {
    setSearchValue("");
    setResults([]);
    setSelectedPatient(null);
    setListOpen(false);
    onClose();
  }

  function handleSelect(patient: Patient) {
    setSelectedPatient(patient);
    setSearchValue(displayPatientName(patient.name));
    setListOpen(false);
  }

  function handleOk() {
    if (selectedPatient) {
      onConfirm(selectedPatient);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("ResultList.changePatient")}
      centered
      lockScroll={false}
    >
      <Stack gap="md">
        <TextInput
          label={
            <Text fz="sm" fw={500}>
              {t("SelectPatient")}
            </Text>
          }
          placeholder={t("NewTest.Placeholder")}
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.currentTarget.value);
            setSelectedPatient(null);
          }}
          rightSection={
            searchValue ? (
              <CloseButton
                onClick={() => {
                  setSearchValue("");
                  setSelectedPatient(null);
                  setResults([]);
                  setListOpen(false);
                }}
              />
            ) : null
          }
          onFocus={() => {
            if (searchValue.length > 2) setListOpen(true);
          }}
        />

        <Box h={200}>
          {listOpen && results.length > 0 && !selectedPatient ? (
            <ScrollArea h={200}>
              <Button.Group orientation="vertical">
                {results.map((patient) => (
                  <Button
                    key={patient.uuid}
                    onClick={() => handleSelect(patient)}
                    variant="default"
                    display="flex"
                    style={{ justifyContent: "space-between" }}
                    styles={{
                      inner: { justifyContent: "space-between", width: "100%" },
                      label: { width: "100%" },
                    }}
                    w="100%"
                  >
                    <Flex w="100%" justify="space-between">
                      <Group wrap="nowrap">
                        <IconUser size={rem(16)} />
                        <Text ta="left">
                          {displayPatientName(patient.name)} {formatDateOnly(patient.dob)}
                        </Text>
                      </Group>
                      <Group wrap="nowrap" justify="flex-end">
                        <IconHash size={rem(16)} />
                        <Tooltip label={"ID: " + patient.uuid} position="left" withArrow>
                          <Text w="70%" ta="right" truncate>
                            {patient.uuid}
                          </Text>
                        </Tooltip>
                      </Group>
                    </Flex>
                  </Button>
                ))}
              </Button.Group>
            </ScrollArea>
          ) : selectedPatient ? (
            <PatientInfoCard value={selectedPatient} hidePrescription />
          ) : null}
        </Box>

        <Group justify="flex-end" mt="sm">
          <Button variant="outline" onClick={handleClose}>
            {t("cancel")}
          </Button>
          <Button disabled={!selectedPatient} onClick={handleOk}>
            {t("ok", "OK")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
